/**
 * MER-636 / MER-626 / MER-613: bucket-aware window bounds for the tables whose
 * rows ARE periods (kpi_summary, route_kpis, financial_health).
 *
 * THE DEFECT. Those rows are stamped at the START of the period they describe:
 * normalize.py anchors a monthly row on the 1st and a weekly row on that
 * week's Monday. applyTimeline windowed them with a bare
 * `gte(column, today - N days)`, which tests the ANCHOR against the window
 * instead of the PERIOD the anchor stands for. An anchor is a single day; a
 * bucket is 7 or ~30 of them. Comparing the wrong one has three consequences,
 * all confirmed against production on 2026-09-09:
 *
 *   1. Monthly grain on a 30-day window renders BLANK or near-blank for the
 *      whole of the following month. On 9 September the cutoff is 10 August,
 *      so the 1 August anchor fails and only the in-progress September bucket
 *      survives. DWS L1.0d "Revenue Waterfall. Cash Collected" summed
 *      $0.00 that way; August was $1,439,028.36. Zay hit the blank version
 *      live: "if I click default with one of the ranges, it throws the whole
 *      thing off. It doesn't show anything."
 *   2. "Last 30 days" covered 35 days of weekly buckets on some weekdays and
 *      28 on others, because whether the straddling Monday cleared the cutoff
 *      depended on today's day of week. SUM tiles moved with the calendar.
 *   3. `last_90_days` was broken identically and unnoticed. A 5 June cutoff
 *      drops the 1 June anchor and admits a partial September in its place,
 *      so the window answered with two complete months plus a few days:
 *      $2,147,231.35 of DWS revenue where the three complete months it asked
 *      for are $3,931,335.23. Early in a month, when the current bucket is
 *      still empty, the same window returns two months flat. No manifest
 *      widget declares last_90_days, which is why this went unreported: it
 *      only arrives from an explicit L90 selection.
 *
 * THE RULE. A bucket belongs to a window when the window covers MORE THAN HALF
 * of it. Equivalently: snap each end of the window to the nearest bucket
 * boundary. That is an overlap test on the period, not a containment test on
 * the anchor, and it is the only reading under which the answer does not swing
 * with today's day of week:
 *
 *   - The lower bound rounds to the nearest boundary rather than always down.
 *     Always-down (pure overlap, "any bucket the window touches at all") also
 *     fixes symptom 1, but it locks symptom 2 in at its worst: a 30-day window
 *     would return five weekly buckets, 35 days, EVERY day, and
 *     `last_12_months` would return thirteen months. Majority-overlap returns
 *     four weekly buckets and twelve months.
 *   - The upper bound is the start of the bucket containing today, exclusive,
 *     so a trailing window ends at the last COMPLETE bucket. The window is
 *     measured back from THAT boundary, not from today; measuring back from
 *     today would shorten the answer by however far into the current bucket we
 *     happen to be (a 90-day window on 30 September would have returned two
 *     months, not three). This is the MER-421 / MER-304 complete-period rule
 *     that already governs declared-monthly tiles via `last_month`, applied to
 *     the trailing branch that never had it.
 *
 * WHAT THIS IS NOT. It does not change any window's declared WIDTH, so the
 * grainDefaults.mjs ruling that "authored trailing windows keep their declared
 * width" is untouched: `last_90_days` still asks for 90 days. It changes which
 * buckets 90 days SELECTS.
 *
 * IN-PROGRESS CALENDAR RANGES ARE DIFFERENT. `this_week` / `this_month` do not
 * describe a trailing width at all; each NAMES a calendar period, and the
 * answer includes the one we are inside, partial or not. Excluding it would
 * put last week's number under a "Week to date" label. So
 * `CURRENT_BUCKET_RANGES` resolves against the named period's own span, with
 * the same nearest-boundary snapping the trailing branch uses. (Before this,
 * weekly `this_week` ran the 7-day trailing branch, which on a Monday or
 * Tuesday cleared TWO Monday anchors and showed two weeks under a WTD label.)
 *
 * WHEN THE GRAIN IS COARSER THAN THE RANGE, THE ANSWER IS NOTHING. Week to
 * date on MONTHLY rows is the live case and the one the first cut of this
 * module got wrong: it returned the containing month, bounds byte-identical to
 * MTD, under a face that still read "This week" (35 DWS / 34 Best Disposal /
 * 35 simonsjunk tiles reach that state on one WTD click). $164.87 of September
 * today, a full month by month end, and the same fabrication class this module
 * exists to remove. No monthly bucket is a week, so `empty` is the honest
 * answer, and the renderer turns `empty` into copy that names WHICH two
 * settings disagree instead of blaming a quiet period. The same rule covers
 * the trailing side: a window narrower than half a bucket selects nothing.
 *
 * A grain FINER than the named range is fine and is answered normally --
 * `this_month` at weekly grain is every weekly bucket September mostly covers.
 *
 * Pure and dependency-free so node:test imports it directly (the
 * tenantScope.mjs / nowCutoff.mjs precedent); periodWindow.d.mts types it for
 * queries.ts. All calendar math runs on LOCAL date parts and never
 * toISOString, so a UTC offset cannot shift a boundary by a day
 * (nowCutoffIsoBound's convention).
 */

import { parseMonthToken } from "./calendarMonth.mjs";

/** Grains whose buckets this module can measure. Anything else -> null. */
const KNOWN_GRAINS = new Set(["monthly", "weekly", "daily"]);

/**
 * Ranges that name the bucket currently in progress rather than a trailing
 * window. These resolve to that single bucket, partial included.
 */
export const CURRENT_BUCKET_RANGES = new Set(["this_week", "this_month"]);

function startOfDay(d) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function addDays(d, n) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
}

function startOfMonth(d) {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

function addMonths(d, n) {
  return new Date(d.getFullYear(), d.getMonth() + n, 1);
}

/** Monday of `d`'s week -- the anchor normalize.py stamps on weekly rows. */
function startOfWeek(d) {
  const s = startOfDay(d);
  // getDay() is 0 for Sunday; shift so Monday is 0.
  return addDays(s, -((s.getDay() + 6) % 7));
}

/**
 * Whole days from `a` to `b`. Compares UTC midnights built from LOCAL calendar
 * parts, so a DST transition inside the span cannot round the answer off.
 */
function dayDiff(a, b) {
  const ua = Date.UTC(a.getFullYear(), a.getMonth(), a.getDate());
  const ub = Date.UTC(b.getFullYear(), b.getMonth(), b.getDate());
  return Math.round((ub - ua) / 86400000);
}

function isoDate(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function monthKey(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

/**
 * Serialize a boundary to match the column's storage type.
 *
 * The DWS schema convention (monthBounds in queries.ts states it): a column
 * whose name ends in `_date` is a real DATE; everything else is TEXT holding
 * a bare YYYY-MM key. A full ISO date sent at a TEXT column sorts
 * lexicographically AFTER the bare month it belongs to ('2026-08' <
 * '2026-08-10'), which is the MER-138 failure and is exactly how
 * financial_health.period_start lost every August row.
 */
function serialize(d, column) {
  return String(column).endsWith("_date") ? isoDate(d) : monthKey(d);
}

/** A TEXT period column (no `_date` suffix) stores a bare YYYY-MM month key. */
function isMonthKeyColumn(column) {
  return !String(column).endsWith("_date");
}

/** Bucket helpers per grain: where a bucket starts, and the next one along. */
const GRAIN = {
  monthly: { start: startOfMonth, next: (d) => addMonths(d, 1) },
  weekly: { start: startOfWeek, next: (d) => addDays(d, 7) },
  daily: { start: startOfDay, next: (d) => addDays(d, 1) },
};

/** Coarseness order, so "is this grain wider than that one" is a comparison. */
const GRAIN_WIDTH = { daily: 0, weekly: 1, monthly: 2 };

/**
 * The calendar period each in-progress range NAMES. `this_week` names a week
 * whatever the rows are bucketed by; `this_month` names a month.
 */
const CURRENT_RANGE_GRAIN = { this_week: "weekly", this_month: "monthly" };

/**
 * Snap `d` to the NEAREST boundary of `bucket`. This is the single rounding
 * rule the whole module runs on, applied to both ends of every window:
 * a bucket is in when the window covers more than half of it.
 *
 * Ties round up (toward the later boundary), which is what excludes an
 * exactly-half-covered bucket at the window's start and includes one at its
 * end -- consistently "round half up" along the timeline.
 */
function snapNearest(d, bucket) {
  const start = bucket.start(d);
  const end = bucket.next(start);
  const width = dayDiff(start, end);
  const into = dayDiff(start, d);
  return into * 2 >= width ? end : start;
}

/**
 * Inclusive-lower / exclusive-upper ANCHOR bounds for a widget's window over
 * period-bucketed rows.
 *
 * @param {{
 *   grain: string | undefined,
 *   range: string | undefined,
 *   days: number | undefined,
 *   column: string,
 *   today: Date,
 * }} input
 *   `grain` is the effective filter.period_type; `days` the resolved
 *   TIMELINE_DAY_RANGES width for `range`.
 * @returns {{ gte: string, lt: string, empty: boolean } | null}
 *   null = this module has no opinion (unknown grain, unknown range); the
 *   caller keeps its previous behaviour. `empty: true` = the window is
 *   narrower than half a bucket, so no bucket qualifies; `gte` equals `lt`.
 */
export function periodWindowBounds(input) {
  const { grain, range, days, column, today } = input ?? {};
  if (!grain || !KNOWN_GRAINS.has(grain)) return null;

  // A TEXT period column stores a bare YYYY-MM key, so the finest window it
  // can EXPRESS is a calendar month. Serializing a weekly or daily boundary
  // into one produces a month-wide window wearing a weekly label -- e.g.
  // weekly + last_30_days on financial_health.period_start emitted
  // ['2026-08', '2026-09'), and on a column holding weekly anchors as
  // 'YYYY-MM-DD' strings that same window would sweep in FIVE August weeks
  // against the four it asked for. Reachable today: L1.1b.1 and L1b.6 sit on
  // financial_health.period_start and declare no period_type, so the MER-362
  // auto-apply hands them whatever the Period selector says. Rather than
  // invent a window the column cannot hold, have no opinion and let the
  // caller keep its pre-existing bare cutoff. That cutoff is not RIGHT on a
  // TEXT column either (it is the MER-138 lexicographic failure); the two
  // widgets should be re-pointed at period_date like their four siblings on
  // the same table, which is MER-650. This guard only stops us adding a new
  // wrong answer on top of the old one.
  if (grain !== "monthly" && isMonthKeyColumn(column)) return null;

  const bucket = GRAIN[grain];

  // MER-692: an explicit calendar month (`month:YYYY-MM`, from `?range=YYYY-MM`).
  // It names a fixed calendar period, so it resolves the way `this_month`
  // does, against that month's own span, but anchored on the named month
  // instead of today: the same token selects the same buckets on any day.
  //   monthly grain -> exactly [month start, next month start)
  //   weekly grain  -> every week the month MOSTLY covers (nearest-boundary
  //                    snap, the rule this_month uses at finer grain)
  //   daily grain   -> the month's days
  // A month is the widest named range and every grain here is at most a
  // month wide, so this branch can never come back empty. The TEXT month-key
  // guard above still applies first: a weekly or daily window cannot be
  // expressed in a bare YYYY-MM column. A future month is answered with its
  // own bounds like any other; the now-cutoff is what keeps forward-dated
  // rows out, and the picker never offers one.
  const month = parseMonthToken(range);
  if (month) {
    const named = new Date(Number(month.slice(0, 4)), Number(month.slice(5, 7)) - 1, 1);
    const gte = snapNearest(named, bucket);
    const lt = snapNearest(addMonths(named, 1), bucket);
    if (dayDiff(gte, lt) <= 0) {
      const key = serialize(gte, column);
      return { gte: key, lt: key, empty: true };
    }
    return { gte: serialize(gte, column), lt: serialize(lt, column), empty: false };
  }

  // In-progress calendar range. `this_week` / `this_month` name a calendar
  // period rather than a trailing width, and the answer covers that period
  // including the part still in progress.
  if (range && CURRENT_BUCKET_RANGES.has(range)) {
    const namedGrain = CURRENT_RANGE_GRAIN[range];
    // A range added to CURRENT_BUCKET_RANGES but not to CURRENT_RANGE_GRAIN
    // has no calendar period to resolve against. Have no opinion rather than
    // resolve against `undefined`.
    if (!namedGrain) return null;
    if (GRAIN_WIDTH[grain] > GRAIN_WIDTH[namedGrain]) {
      // The rows are bucketed COARSER than the period the range names: week
      // to date on monthly rows. No monthly bucket is a week, so no bucket
      // qualifies. Returning the containing month here is what shipped in the
      // first cut of this module, and it put a whole month (growing to a full
      // month's revenue by month end) under a "This week" face on 35 DWS
      // tiles. Say nothing fits instead; grainMismatchCopy turns this into
      // copy naming the grain and the range.
      const key = serialize(bucket.start(today), column);
      return { gte: key, lt: key, empty: true };
    }
    // Same grain, or finer. Same-grain snaps to itself (WTD on weekly rows is
    // this Monday to next). Finer rounds to the nearest bucket boundary, so
    // MTD on weekly rows is every week September mostly covers.
    const named = GRAIN[namedGrain];
    const from = named.start(today);
    const gte = snapNearest(from, bucket);
    const lt = snapNearest(named.next(from), bucket);
    if (dayDiff(gte, lt) <= 0) {
      const key = serialize(gte, column);
      return { gte: key, lt: key, empty: true };
    }
    return { gte: serialize(gte, column), lt: serialize(lt, column), empty: false };
  }

  if (!days || days <= 0) return null;

  // Trailing window: ends at the last COMPLETE bucket, and is measured back
  // from that boundary.
  const end = bucket.start(today);
  const start = snapNearest(addDays(end, -days), bucket);

  if (dayDiff(start, end) <= 0) {
    // Narrower than half a bucket. Emit an empty range rather than a bucket
    // the caller did not ask for.
    const key = serialize(end, column);
    return { gte: key, lt: key, empty: true };
  }

  return { gte: serialize(start, column), lt: serialize(end, column), empty: false };
}

/**
 * MER-636, the copy half. When a tile comes back empty because the selected
 * range cannot address a single bucket at its grain, the generic empty-range
 * line ("Limited activity this period. Try a wider range.") describes the
 * wrong thing: activity is not the issue, and "wider" is only half the remedy.
 *
 * Week to date on monthly rows is the live case and the reason this exists.
 * Clicking WTD puts 35 DWS / 34 Best Disposal / 35 simonsjunk monthly tiles
 * into it at once, and the honest answer there is nothing, so the tile has to
 * say which two settings disagree and name the Period selector as the remedy.
 *
 * The probe runs on `period_date` deliberately: it is asking whether the
 * ARITHMETIC has an answer, not whether some particular column could store
 * it. A TEXT-column widget that lost its bounds to the month-key guard falls
 * back to the pre-MER-636 cutoff and keeps the generic empty copy.
 *
 * Returns the replacement copy, or null to leave the widget's existing empty
 * state alone. Deliberately narrow: it fires only for the arithmetic case this
 * ticket created, never as a blanket rewrite of the empty state. EMPTY_RANGE_COPY
 * still owns every genuinely sparse range (wording owned by AJ).
 *
 * @param {{ grain: string | undefined, range: string | undefined, days: number | undefined }} input
 * @returns {string | null}
 */
export function grainMismatchCopy(input) {
  const bounds = periodWindowBounds({
    ...(input ?? {}),
    column: "period_date",
    today: new Date(),
  });
  if (!bounds || !bounds.empty) return null;
  const grain = input.grain;
  const opener = `No ${grain} period fits the selected range.`;
  return grain === "monthly"
    ? `${opener} Widen the range, or set the period to weekly.`
    : `${opener} Widen the range.`;
}
